import React, { useState } from "react";
import { Settings2, RotateCcw, PanelTop, LayoutList, Rows3, PanelBottom, Coins } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { getConfig, saveConfig, resetConfig, DEFAULT_CONFIG, getCredits, setCredits } from "../mock/mock";
import UsersPanel from "../components/UsersPanel";

const MENU_LABELS = {
  editor: { label: "Editor", desc: "Enlace directo al editor de vídeo" },
  mediateca: { label: "Mediateca", desc: "Repositorio de vídeos subidos y procesados" },
  herramientas: { label: "Herramientas", desc: "Menú desplegable con todas las herramientas" },
  casos: { label: "Casos de uso", desc: "Menú desplegable de sectores y ejemplos" },
  tarifas: { label: "Tarifas", desc: "Enlace a la página de precios" },
  ayuda: { label: "Ayuda", desc: "FAQ, guías y contacto" },
};

const HEADER_LABELS = {
  idioma: { label: "Selector de idioma", desc: "Botón ES en la cabecera" },
  creditos: { label: "Contador de créditos", desc: "Saldo visible junto al menú" },
  ctaEditor: { label: "Botón «Ir al editor»", desc: "CTA principal de la cabecera" },
};

const SECTION_LABELS = {
  hero: { label: "Hero principal", desc: "Portada con titular y visual" },
  stats: { label: "Estadísticas", desc: "Cifras de confianza" },
  herramientas: { label: "Herramientas", desc: "Cuadrícula de funciones" },
  comoFunciona: { label: "Cómo funciona", desc: "Los tres pasos" },
  casos: { label: "Casos de uso", desc: "Carrusel de sectores" },
  tarifas: { label: "Tarifas", desc: "Planes y precios en la home" },
  testimonios: { label: "Testimonios", desc: "Opiniones de usuarios" },
  faq: { label: "Preguntas frecuentes", desc: "Acordeón de dudas" },
  ctaFinal: { label: "CTA final", desc: "Banner naranja de cierre" },
};

const Row = ({ label, desc, checked, onChange }) => (
  <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-[#F5EDE3] last:border-0">
    <div>
      <p className="text-[14px] font-extrabold text-[#241E18]">{label}</p>
      <p className="text-[12.5px] text-[#8A7F73]">{desc}</p>
    </div>
    <Switch
      checked={checked}
      onCheckedChange={onChange}
      className="data-[state=checked]:bg-[#F26F21]"
    />
  </div>
);

const Card = ({ icon: Icon, title, children }) => (
  <div className="rounded-3xl border border-[#F0E7DD] bg-white overflow-hidden">
    <div className="flex items-center gap-3 px-5 py-4 bg-[#FFF7F0] border-b border-[#F0E7DD]">
      <span className="w-9 h-9 rounded-xl bg-[#F26F21] text-white flex items-center justify-center">
        <Icon size={17} />
      </span>
      <h2 className="font-display text-[16px] font-extrabold text-[#241E18]">{title}</h2>
    </div>
    {children}
  </div>
);

const AdminPage = () => {
  const [config, setConfig] = useState(getConfig());
  const [credits, setCreditsState] = useState(getCredits());
  const [creditsInput, setCreditsInput] = useState("");

  const applyCredits = (n) => {
    const v = Math.max(0, Math.floor(n));
    setCredits(v);
    setCreditsState(v);
    setCreditsInput("");
    toast.success(`Saldo actualizado: ${v.toLocaleString("es-ES")} créditos`);
  };

  const update = (group, key, value) => {
    const next = { ...config, [group]: { ...config[group], [key]: value } };
    setConfig(next);
    saveConfig(next);
    toast.success(value ? "Elemento activado" : "Elemento desactivado");
  };

  const restore = () => {
    resetConfig();
    setConfig(JSON.parse(JSON.stringify(DEFAULT_CONFIG)));
    toast.success("Configuración restaurada");
  };

  return (
    <main className="min-h-[calc(100vh-72px)] bg-[#FFF7F0] py-12">
      <div className="max-w-[880px] mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FDEBDC] text-[#C2570F] text-[12px] font-extrabold uppercase tracking-wider mb-4">
              <Settings2 size={12} /> Backoffice
            </p>
            <h1 className="font-display text-[30px] sm:text-[38px] font-extrabold text-[#241E18] leading-tight">
              Panel de control
            </h1>
            <p className="text-[14px] text-[#8A7F73] mt-2">
              Activa o desactiva menús, elementos y secciones. Los cambios se aplican al instante.
            </p>
          </div>
          <button
            onClick={restore}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full border-2 border-[#241E18]/15 text-[#241E18] text-[13.5px] font-bold hover:border-[#F26F21] hover:text-[#F26F21] transition-all"
          >
            <RotateCcw size={14} /> Restaurar todo
          </button>
        </div>

        <UsersPanel />

        <div className="space-y-6">
          <Card icon={Coins} title="Créditos de la cuenta">
            <div className="px-5 py-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[14px] font-extrabold text-[#241E18]">Saldo actual</p>
                  <p className="text-[12.5px] text-[#8A7F73]">1 crédito = 1 segundo de vídeo procesado</p>
                </div>
                <span className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-[#FFF3E9] border border-[#FBD9BC] text-[#C2570F] font-extrabold text-[15px]">
                  <Coins size={15} /> {credits.toLocaleString("es-ES")}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="number"
                  min="0"
                  value={creditsInput}
                  onChange={(e) => setCreditsInput(e.target.value)}
                  placeholder="Nuevo saldo…"
                  className="h-10 w-40 px-3 rounded-xl border border-[#F0E2D2] text-[13.5px] font-bold text-[#241E18] focus:outline-none focus:border-[#F26F21]"
                />
                <button
                  onClick={() => creditsInput !== "" && applyCredits(Number(creditsInput))}
                  className="h-10 px-5 rounded-full bg-[#241E18] text-white text-[13px] font-bold hover:bg-[#F26F21] transition-colors"
                >
                  Establecer
                </button>
                {[100, 5000, 20000].map((n) => (
                  <button
                    key={n}
                    onClick={() => applyCredits(n)}
                    className="h-10 px-4 rounded-full border border-[#F0E2D2] bg-white text-[13px] font-bold text-[#57503F] hover:border-[#F26F21] transition-colors"
                  >
                    {n.toLocaleString("es-ES")}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          <Card icon={PanelTop} title="Menús de navegación">
            {Object.entries(MENU_LABELS).map(([key, m]) => (
              <Row key={key} label={m.label} desc={m.desc} checked={config.menus[key]} onChange={(v) => update("menus", key, v)} />
            ))}
          </Card>

          <Card icon={LayoutList} title="Elementos de la cabecera">
            {Object.entries(HEADER_LABELS).map(([key, m]) => (
              <Row key={key} label={m.label} desc={m.desc} checked={config.header[key]} onChange={(v) => update("header", key, v)} />
            ))}
          </Card>

          <Card icon={Rows3} title="Secciones de la página de inicio">
            {Object.entries(SECTION_LABELS).map(([key, m]) => (
              <Row key={key} label={m.label} desc={m.desc} checked={config.sections[key]} onChange={(v) => update("sections", key, v)} />
            ))}
          </Card>

          <Card icon={PanelBottom} title="Pie de página">
            <Row
              label="Mostrar footer"
              desc="Pie de página completo con enlaces y redes"
              checked={config.footer.visible}
              onChange={(v) => update("footer", "visible", v)}
            />
          </Card>
        </div>

        <p className="text-[12px] text-[#A69C90] mt-8 text-center">
          La configuración se guarda en este navegador (demo). En producción se sincronizaría con el servidor.
        </p>
      </div>
    </main>
  );
};

export default AdminPage;
